/**
 * Cross-workspace Cmd+K command palette.
 *
 * Opens on Cmd+K / Ctrl+K from anywhere in the app. Searches across
 * every workspace's nav items (portfolio, project, contract, supply
 * chain, timesheet, config) plus a small set of high-value action
 * verbs (Switch portfolio, Open AI panel, etc.).
 *
 * Pure React, no extra deps. Substring + word-boundary matching with
 * prefix-boost and recency-boost. Last-used IDs persist in localStorage
 * (`ppm-cmdk-recent`).
 *
 * Records: when Workflow and Forms is in the user's modules, a query of two or
 * more letters or digits also searches requests, library documents and forms on
 * the server (wff_search, migration 00539) with the signed-in person's own
 * access, and lists the best matches under "Records" with a link to the full
 * Search page (docs/REPORTING_AND_SEARCH.md).
 *
 * IT Ticketing: on any /it-ticketing page the palette leads with live module
 * data (tickets, people, comments; modules/it-ticketing/services/
 * ticketingSearch.ts) and the module's own pages, and an empty query lists
 * those pages instead of recents.
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Search, ArrowRight, ArrowUp, ArrowDown, CornerDownLeft,
  LayoutDashboard, FolderKanban, DollarSign, Users, FileInput, Target,
  FlaskConical, GitBranch, BarChart3, AlertTriangle, GanttChart,
  TrendingUp, Wallet, FileText, Network, Activity, ClipboardList,
  Settings, Shield, UserCog, ScrollText, KeyRound, Monitor,
  Building2, Cable, PackageSearch, Milestone, Clock, ClipboardCheck, History,
  Workflow, Sparkles, Home, ShieldCheck,
  Factory, Gauge, Handshake, Radar, Wrench, Scale, FileInput as FormIcon, Table2, ListOrdered,
  Ticket, MessageSquare, UserRound,
} from 'lucide-react'
import { useAppStore } from '@/store/appStore'
import { isPathAllowed, useModuleEntitlement } from '@/config/moduleEntitlements'
import { getFinancePageLabel } from '@/lib/projectClassification'
import { cn } from '@/lib/utils'
import { CuiChip } from '@/components/security/CuiBanner'
import { hitLink, hitSubtitle, searchable, searchRecords, type SearchHit } from '@/services/reports/searchService'
import { isItTicketingPath } from '@/modules/it-ticketing/lib/itTicketingRoutes'

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

interface CommandEntry {
  id: string
  title: string
  /** Workspace bucket — drives the section header and color tag. */
  workspace: 'Portfolio' | 'Project' | 'Contract' | 'Supply Chain' | 'Timesheet' | 'IT Ticketing' | 'Workflow' | 'Configuration' | 'Action' | 'Tool' | 'Records'
    | 'Tickets' | 'People' | 'Comments'
  icon: React.ReactNode
  keywords?: string[]
  /** Second line (record hits): form, status, submitter or library, revision. */
  subtitle?: string
  /** A controlled (CUI) record: marked, and never kept in the recent list. */
  cui?: boolean
  /** Server search results are not remembered as recents. */
  transient?: boolean
  /** Either a path to navigate to, or an action callback (mutually exclusive). */
  path?: string
  action?: () => void
}

/**
 * Build the command catalog. Action callbacks pull live state from the store
 * each invocation, which is why this is a function not a constant.
 */
function buildCatalog(): CommandEntry[] {
  return [
    // Portfolio
    { id: 'p-dashboard',          workspace: 'Portfolio',    title: 'Portfolio Dashboard',           icon: <LayoutDashboard size={16} />, path: '/dashboard',           keywords: ['kpi','overview','health'] },
    { id: 'p-budget',             workspace: 'Portfolio',    title: 'Portfolio Budgeting',           icon: <DollarSign size={16} />,      path: '/budget-releases',     keywords: ['fund','releases'] },
    { id: 'p-scenarios',          workspace: 'Portfolio',    title: 'Scenario Planning',             icon: <FlaskConical size={16} />,    path: '/scenario-planning',   keywords: ['what-if','simulate'] },
    { id: 'p-resources',          workspace: 'Portfolio',    title: 'Resource Consumption',          icon: <Users size={16} />,           path: '/resource-consumption' },
    { id: 'p-projects',           workspace: 'Portfolio',    title: 'Active Projects',               icon: <FolderKanban size={16} />,    path: '/projects',            keywords: ['list','search projects'] },
    { id: 'p-requests',           workspace: 'Portfolio',    title: 'Project Requests',              icon: <FileInput size={16} />,       path: '/project-requests',    keywords: ['intake','demand'] },
    { id: 'p-priorities',         workspace: 'Portfolio',    title: 'Priorities & KPIs',             icon: <Target size={16} />,          path: '/priorities' },
    { id: 'p-workflows',          workspace: 'Portfolio',    title: 'Workflow',                      icon: <GitBranch size={16} />,       path: '/workflows' },
    { id: 'p-analysis',           workspace: 'Portfolio',    title: 'Analysis & Attributes',         icon: <BarChart3 size={16} />,       path: '/analysis' },
    { id: 'p-risks',              workspace: 'Portfolio',    title: 'Risks, Issues & Opportunities', icon: <AlertTriangle size={16} />,   path: '/risks' },
    { id: 'p-milestones',         workspace: 'Portfolio',    title: 'Milestone Analyzer',            icon: <Milestone size={16} />,       path: '/milestone-analyzer' },
    { id: 'p-program-review',     workspace: 'Portfolio',    title: 'Program Review',                icon: <ClipboardCheck size={16} />,  path: '/programs/review' },

    // Project tools (require a project to be selected, but we surface them anyway)
    { id: 't-gantt',              workspace: 'Project',      title: 'Scheduling (Gantt)',            icon: <GanttChart size={16} />,      path: '/gantt' },
    { id: 't-baseline',           workspace: 'Project',      title: 'Baseline Management',           icon: <Wallet size={16} />,          path: '/finance',             keywords: ['pmb','budget'] },
    { id: 't-forecasts',          workspace: 'Project',      title: 'Forecast Management',           icon: <Activity size={16} />,        path: '/forecasts',           keywords: ['eac','etc'] },
    { id: 't-evm',                workspace: 'Project',      title: 'Progress & EVM Workbench',      icon: <TrendingUp size={16} />,      path: '/evm',                 keywords: ['cpi','spi','earned value'] },
    { id: 't-hierarchy',          workspace: 'Project',      title: 'WBS Hierarchy',                 icon: <Network size={16} />,         path: '/hierarchy' },
    { id: 't-reports',            workspace: 'Project',      title: 'Project Reports',               icon: <BarChart3 size={16} />,       path: '/reports',             keywords: ['cpr','ipmdar','cfsr'] },

    // Contract
    { id: 'c-dashboard',          workspace: 'Contract',     title: 'Contract Dashboard',            icon: <LayoutDashboard size={16} />, path: '/contracts',           keywords: ['clm'] },
    { id: 'c-all',                workspace: 'Contract',     title: 'All Contracts',                 icon: <FileText size={16} />,        path: '/contracts/all' },
    { id: 'c-approvals',          workspace: 'Contract',     title: 'Contract Approvals',            icon: <ClipboardList size={16} />,   path: '/contracts/approvals' },
    { id: 'c-templates',          workspace: 'Contract',     title: 'Clause Templates',              icon: <ScrollText size={16} />,      path: '/contracts/templates' },
    { id: 'c-terms',              workspace: 'Contract',     title: 'Preferred Terms',               icon: <Shield size={16} />,          path: '/contracts/preferred-terms' },
    { id: 'c-customers',          workspace: 'Contract',     title: 'Customer Analysis',             icon: <Building2 size={16} />,       path: '/contracts/customers' },
    { id: 'c-analytics',          workspace: 'Contract',     title: 'Workflow Analytics (CLM)',      icon: <BarChart3 size={16} />,       path: '/contracts/analytics' },

    // Supply Chain — the thirteen planning domains (feature-catalog A..M).
    { id: 'sc-dashboard',         workspace: 'Supply Chain', title: 'Planning Home',                 icon: <LayoutDashboard size={16} />, path: '/supply-chain',                 keywords: ['scp','supply chain','overview'] },
    { id: 'sc-demand',            workspace: 'Supply Chain', title: 'Demand Workbench',              icon: <TrendingUp size={16} />,      path: '/supply-chain/demand',          keywords: ['clin','pipeline','pwin','idiq'] },
    { id: 'sc-supply',            workspace: 'Supply Chain', title: 'Supply & MRP',                  icon: <Factory size={16} />,         path: '/supply-chain/supply',          keywords: ['mrp','net requirements','make buy','long lead'] },
    { id: 'sc-inventory',         workspace: 'Supply Chain', title: 'Inventory & DDMRP',             icon: <PackageSearch size={16} />,   path: '/supply-chain/inventory',       keywords: ['buffer','safety stock','meio','shelf life'] },
    { id: 'sc-capacity',          workspace: 'Supply Chain', title: 'Capacity Planning',             icon: <Gauge size={16} />,           path: '/supply-chain/capacity',        keywords: ['rccp','work center','bottleneck','nadcap'] },
    { id: 'sc-gpd',               workspace: 'Supply Chain', title: 'GPD & Pegging',                 icon: <Network size={16} />,         path: '/supply-chain/gpd',             keywords: ['grouping','pegging','distribution','allocation','pmmo'] },
    { id: 'sc-promising',         workspace: 'Supply Chain', title: 'Order Promising (ATP/CTP)',     icon: <Handshake size={16} />,       path: '/supply-chain/promising',       keywords: ['atp','ctp','promise','allocated'] },
    { id: 'sc-response',          workspace: 'Supply Chain', title: 'Scenarios & Response',          icon: <GitBranch size={16} />,       path: '/supply-chain/response',        keywords: ['what-if','re-peg','simulate','concurrent'] },
    { id: 'sc-control-tower',     workspace: 'Supply Chain', title: 'Control Tower',                 icon: <Radar size={16} />,           path: '/supply-chain/control-tower',   keywords: ['exception','triage','mrp message','alert'] },
    { id: 'sc-supplier',          workspace: 'Supply Chain', title: 'Supplier & SCFM',               icon: <ClipboardList size={16} />,   path: '/supply-chain/supplier',        keywords: ['commit','scorecard','flowdown','subcontract'] },
    { id: 'sc-compliance',        workspace: 'Supply Chain', title: 'Compliance & Traceability',     icon: <Shield size={16} />,          path: '/supply-chain/compliance',      keywords: ['berry','specialty metals','itar','iuid','dpas','cmmc','genealogy'] },
    { id: 'sc-sustainment',       workspace: 'Supply Chain', title: 'Sustainment & MRO',             icon: <Wrench size={16} />,          path: '/supply-chain/sustainment',     keywords: ['spares','pbl','rotable','dmsms','availability'] },
    { id: 'sc-sop',               workspace: 'Supply Chain', title: 'S&OP / IBP',                    icon: <Scale size={16} />,           path: '/supply-chain/sop',             keywords: ['sop','ibp','p&l','progress payment','earned value','funding','color of money'] },

    // IT Ticketing
    { id: 'it-tickets',           workspace: 'IT Ticketing', title: 'Ticket Queue',                  icon: <Ticket size={16} />,          path: '/it-ticketing',                         keywords: ['tickets','help desk','support','it'] },
    { id: 'it-reports',           workspace: 'IT Ticketing', title: 'Ticket Reports',                icon: <BarChart3 size={16} />,       path: '/it-ticketing/reports',                 keywords: ['kpi','breakdown','report cards'] },
    { id: 'it-time',              workspace: 'IT Ticketing', title: 'Ticket Time Tracking',          icon: <Clock size={16} />,           path: '/it-ticketing/reports/time',            keywords: ['queue time','chess clock','sla','duration'] },
    { id: 'it-history',           workspace: 'IT Ticketing', title: 'Ticket History',                icon: <History size={16} />,         path: '/it-ticketing/history',                 keywords: ['audit','changes','change log','who changed'] },
    { id: 'it-admin',             workspace: 'IT Ticketing', title: 'Ticketing Admin',               icon: <ShieldCheck size={16} />,  path: '/it-ticketing/admin',                    keywords: ['settings','admins','categories','customers','agents','rbac'] },
    { id: 'it-rules',             workspace: 'IT Ticketing', title: 'Ticket Workflow Rules',         icon: <Workflow size={16} />,     path: '/it-ticketing/admin?adminTab=workflow-rules', keywords: ['approvals','approver','admin'] },
    { id: 'it-roles',             workspace: 'IT Ticketing', title: 'Ticket Role Permissions',       icon: <ShieldCheck size={16} />,  path: '/it-ticketing/admin?adminTab=roles',     keywords: ['rbac','agent','requester','admin'] },
    { id: 'it-fields',            workspace: 'IT Ticketing', title: 'Ticket Field Settings',         icon: <Settings size={16} />,     path: '/it-ticketing/admin?adminTab=fields',    keywords: ['custom fields','admin'] },
    { id: 'it-delegations',       workspace: 'IT Ticketing', title: 'My Ticket Delegations',         icon: <UserCog size={16} />,      path: '/it-ticketing/my-delegations',           keywords: ['out of office','approver','delegate'] },

    // Timesheet
    { id: 'ts-dashboard',        workspace: 'Timesheet',    title: 'Timesheet Dashboard',           icon: <LayoutDashboard size={16} />, path: '/timesheet' },
    { id: 'ts-my',                workspace: 'Timesheet',    title: 'My Timesheet',                  icon: <Clock size={16} />,           path: '/timesheet/my' },
    { id: 'ts-approvals',         workspace: 'Timesheet',    title: 'Timesheet Approvals',           icon: <ClipboardCheck size={16} />,  path: '/timesheet/approvals' },
    { id: 'ts-reports',           workspace: 'Timesheet',    title: 'Timesheet Reports',             icon: <BarChart3 size={16} />,       path: '/timesheet/reports' },
    { id: 'ts-floor',             workspace: 'Timesheet',    title: 'Floor Check',                   icon: <ShieldCheck size={16} />,     path: '/timesheet/floor-check', keywords: ['dcaa','audit'] },
    { id: 'ts-settings',          workspace: 'Timesheet',    title: 'Timesheet Settings',            icon: <Settings size={16} />,        path: '/timesheet/settings' },
    // Timesheet — Admin & Reporting surfaces, indexed here so a manager/admin
    // can jump straight to one by name without knowing it lives under the hub.
    { id: 'ts-admin',             workspace: 'Timesheet',    title: 'Admin & Reporting',             icon: <LayoutDashboard size={16} />, path: '/timesheet/admin',            keywords: ['hub','reporting','manager','admin'] },
    { id: 'ts-assign',            workspace: 'Timesheet',    title: 'Assign Work',                   icon: <ClipboardCheck size={16} />,  path: '/timesheet/assign-work',      keywords: ['plan','charge targets','manager'] },
    { id: 'ts-subappr',           workspace: 'Timesheet',    title: 'Sub Approvals',                 icon: <ClipboardCheck size={16} />,  path: '/timesheet/sub-approvals',    keywords: ['subcontractor','prime accept','approve'] },
    { id: 'ts-auditchain',        workspace: 'Timesheet',    title: 'Audit Chain Verifier',          icon: <ShieldCheck size={16} />,     path: '/timesheet/audit-chain',      keywords: ['hash','tamper','dcaa','verify'] },
    { id: 'ts-auditlog',          workspace: 'Timesheet',    title: 'Audit Log',                     icon: <ScrollText size={16} />,      path: '/timesheet/audit-log',        keywords: ['export','floor-check pack','dcaa'] },
    { id: 'ts-flooractivity',     workspace: 'Timesheet',    title: 'Floor Activity',                icon: <Activity size={16} />,        path: '/timesheet/floor-activity',   keywords: ['live','wall','who is on what'] },
    { id: 'ts-exceptions',        workspace: 'Timesheet',    title: 'Exceptions Queue',              icon: <AlertTriangle size={16} />,   path: '/timesheet/exceptions',       keywords: ['anomaly','manager'] },
    { id: 'ts-anomaly',           workspace: 'Timesheet',    title: 'Anomaly Dashboard',             icon: <AlertTriangle size={16} />,   path: '/timesheet/anomaly-dashboard' },
    { id: 'ts-pto',               workspace: 'Timesheet',    title: 'PTO Balance',                   icon: <Clock size={16} />,           path: '/timesheet/pto',              keywords: ['leave','vacation'] },
    { id: 'ts-pto-req',           workspace: 'Timesheet',    title: 'PTO Requests',                  icon: <Clock size={16} />,           path: '/timesheet/pto-requests',     keywords: ['leave','approve'] },
    { id: 'ts-teamoff',           workspace: 'Timesheet',    title: 'Team Time Off',                 icon: <Users size={16} />,           path: '/timesheet/team-time-off',    keywords: ['manager','coverage'] },
    { id: 'ts-mycompliance',      workspace: 'Timesheet',    title: 'My Compliance',                 icon: <Shield size={16} />,          path: '/timesheet/my-compliance' },
    { id: 'ts-compliance',        workspace: 'Timesheet',    title: 'Compliance',                    icon: <ShieldCheck size={16} />,     path: '/timesheet/compliance',       keywords: ['cmmc','soc2','fedramp'] },
    { id: 'ts-labordist',         workspace: 'Timesheet',    title: 'Labor Distribution',            icon: <BarChart3 size={16} />,       path: '/timesheet/labor-distribution' },
    { id: 'ts-indirect',          workspace: 'Timesheet',    title: 'Indirect Labor',                icon: <BarChart3 size={16} />,       path: '/timesheet/indirect-labor' },
    { id: 'ts-chargecodes',       workspace: 'Timesheet',    title: 'Charge Codes Catalog',          icon: <ScrollText size={16} />,      path: '/timesheet/charge-codes' },
    { id: 'ts-erprecon',          workspace: 'Timesheet',    title: 'ERP Reconciliation',            icon: <Cable size={16} />,           path: '/timesheet/erp-reconciliation' },
    { id: 'ts-invoices',          workspace: 'Timesheet',    title: 'Customer Invoices',             icon: <FileText size={16} />,        path: '/timesheet/customer-invoices' },

    // Workflow and Forms: reporting and record search (00539)
    { id: 'wm-dashboards',        workspace: 'Workflow',     title: 'Workflow Dashboards',           icon: <BarChart3 size={16} />,       path: '/workflow-management/dashboards', keywords: ['requests','approvals','aging','cycle time','charts'] },
    { id: 'wm-reports',           workspace: 'Workflow',     title: 'Workflow Reports',              icon: <Table2 size={16} />,          path: '/workflow-management/reports',    keywords: ['report builder','export','schedule','requests','approvals','documents','training'] },
    { id: 'wm-request-register',  workspace: 'Workflow',     title: 'Request Register',              icon: <ListOrdered size={16} />,     path: '/workflow-management/reports/request-register', keywords: ['register','waiting on','department','overdue','requests'] },
    { id: 'wm-search',            workspace: 'Workflow',     title: 'Search Records',                icon: <Search size={16} />,          path: '/workflow-management/search',     keywords: ['find request','document number','request number','full text'] },

    // Configuration
    { id: 'cfg-sap',              workspace: 'Configuration', title: 'SAP Integration',              icon: <Cable size={16} />,           path: '/sap-integration' },
    { id: 'cfg-users',            workspace: 'Configuration', title: 'User Management',              icon: <UserCog size={16} />,         path: '/config/users' },
    { id: 'cfg-roles',            workspace: 'Configuration', title: 'Role Assignments',             icon: <KeyRound size={16} />,        path: '/config/roles' },
    { id: 'cfg-policies',         workspace: 'Configuration', title: 'ABAC Policies',                icon: <Shield size={16} />,          path: '/config/policies',      keywords: ['authorization','permissions'] },
    { id: 'cfg-audit',            workspace: 'Configuration', title: 'Audit Log',                    icon: <ScrollText size={16} />,      path: '/config/audit' },
    { id: 'cfg-ui',               workspace: 'Configuration', title: 'UI Policy Mappings',           icon: <Monitor size={16} />,         path: '/config/ui-mappings' },
    { id: 'cfg-lifecycle',        workspace: 'Configuration', title: 'Project Lifecycle',            icon: <Workflow size={16} />,        path: '/config/project-lifecycle' },

    // Quick actions
    { id: 'a-home',               workspace: 'Action',       title: 'Switch workspace / portfolio',  icon: <Home size={16} />,            path: '/',                    keywords: ['change','select','switch'] },
    { id: 'a-ai',                 workspace: 'Action',       title: 'Open AI assistant panel',       icon: <Sparkles size={16} />,        action: () => useAppStore.getState().toggleAiPanel?.() ?? useAppStore.setState({ aiPanelOpen: true }), keywords: ['copilot','chat'] },
    { id: 'a-notifications',      workspace: 'Action',       title: 'Notifications',                 icon: <ClipboardList size={16} />,   path: '/notifications' },
    { id: 'a-help-center',        workspace: 'Action',       title: 'Help Center',                   icon: <FileText size={16} />,        path: '/help',                keywords: ['guide','docs','learn','how to','tour','explain'] },
    { id: 'a-api-explorer',       workspace: 'Action',       title: 'API Explorer (developer)',      icon: <FileText size={16} />,        path: '/developer/api-explorer', keywords: ['developer','swagger','openapi'] },
  ]
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

const RECENT_KEY = 'ppm-cmdk-recent'
const RECENT_LIMIT = 8

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_LIMIT) : []
  } catch { return [] }
}

function pushRecent(id: string) {
  try {
    const current = readRecent().filter(x => x !== id)
    current.unshift(id)
    localStorage.setItem(RECENT_KEY, JSON.stringify(current.slice(0, RECENT_LIMIT)))
  } catch { /* ignore */ }
}

interface Scored {
  entry: CommandEntry
  score: number
}

function scoreEntry(entry: CommandEntry, q: string, recentRank: number): number {
  if (!q) return -recentRank // sort recents to the top when query is empty
  const qLower = q.toLowerCase()
  const titleLower = entry.title.toLowerCase()
  const wsLower = entry.workspace.toLowerCase()
  const haystack = [titleLower, wsLower, ...(entry.keywords ?? []).map(k => k.toLowerCase())]

  let score = -Infinity
  for (const h of haystack) {
    if (h === qLower) score = Math.max(score, 1000)
    else if (h.startsWith(qLower)) score = Math.max(score, 500)
    else if (new RegExp(`\\b${escapeRegex(qLower)}`).test(h)) score = Math.max(score, 200)
    else if (h.includes(qLower)) score = Math.max(score, 50)
  }
  if (score === -Infinity) return -Infinity
  // Recency tiebreaker: -8…-1 (most recent gets +8)
  if (recentRank >= 0) score += (RECENT_LIMIT - recentRank)
  return score
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WORKSPACE_ORDER: Array<CommandEntry['workspace']> = [
  'Portfolio', 'Project', 'Contract', 'Supply Chain', 'Timesheet', 'IT Ticketing', 'Workflow', 'Configuration', 'Action', 'Tool',
]
/** Inside IT Ticketing the module's own pages lead, ahead of every other workspace. */
const TICKETING_WORKSPACE_ORDER: Array<CommandEntry['workspace']> = [
  'IT Ticketing', ...WORKSPACE_ORDER.filter((ws) => ws !== 'IT Ticketing'),
]
/** Live ticket / comment / people matches shown in the palette. */
const TICKETING_LIMIT = 6
/** Server record matches shown in the palette (the Search page lists more). */
const RECORD_LIMIT = 6
const RECORD_ICON: Record<SearchHit['type'], React.ReactNode> = {
  request: <ClipboardList size={16} />,
  document: <FileText size={16} />,
  form: <FormIcon size={16} />,
}

function recordEntry(hit: SearchHit): CommandEntry {
  const title = [hit.number, hit.title].filter(Boolean).join(' · ') || (hit.type === 'form' ? 'Form' : 'Untitled')
  return {
    id: `rec-${hit.type}-${hit.id}`,
    workspace: 'Records',
    title,
    subtitle: hitSubtitle(hit),
    icon: RECORD_ICON[hit.type],
    path: hitLink(hit),
    cui: hit.cui === true,
    transient: true,
  }
}

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [recordQuery, setRecordQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  // Selection-safe backdrop: close only when the press STARTED on the backdrop.
  const pressStartedOnBackdrop = useRef(false)
  const navigate = useNavigate()

  const selectedProject = useAppStore((s) => s.selectedProject)
  const entitlement = useModuleEntitlement()
  const catalog = useMemo(() => {
    // Only offer destinations this user's module entitlement can open.
    const base = buildCatalog().filter((c) => !c.path || isPathAllowed(c.path, entitlement))
    // Apply E&C relabel for /finance so the palette matches the sidebar.
    const projectType = (selectedProject as { projectType?: string | null } | null)?.projectType ?? null
    const financeLabel = getFinancePageLabel(projectType)
    if (financeLabel === 'Baseline Management') return base
    return base.map((c) => c.id === 't-baseline' ? { ...c, title: financeLabel } : c)
  }, [selectedProject, entitlement])
  const recordsAllowed = isPathAllowed('/workflow-management/search', entitlement)
  // Inside IT Ticketing the palette searches the module's live data first
  // (tickets, people, comments); see ticketingSearch.ts.
  const { pathname } = useLocation()
  const ticketingMode = isItTicketingPath(pathname) && isPathAllowed('/it-ticketing', entitlement)

  // Cmd+K / Ctrl+K toggles, Esc closes
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(o => !o)
      } else if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    // Let other chrome (e.g. the navbar search box) open the palette by event,
    // so there's one page-search everyone can reach by click or by Ctrl+K.
    function onOpenEvent() { setOpen(true) }
    window.addEventListener('keydown', onKey)
    window.addEventListener('open-command-palette', onOpenEvent)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('open-command-palette', onOpenEvent)
    }
  }, [open])

  // Focus input + reset state when opening
  useEffect(() => {
    if (open) {
      setQuery('')
      setRecordQuery('')
      setHighlighted(0)
      // RAF so the modal mounts before we focus
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  // Server record search, debounced. Runs with the signed-in person's access.
  useEffect(() => {
    const t = setTimeout(() => setRecordQuery(query.trim()), 250)
    return () => clearTimeout(t)
  }, [query])
  const recordsOn = open && recordsAllowed && searchable(query.trim())
  const recordsQ = useQuery({
    queryKey: ['palette-record-search', recordQuery],
    queryFn: () => searchRecords(recordQuery, {}, RECORD_LIMIT),
    enabled: open && recordsAllowed && searchable(recordQuery),
    retry: false,
    staleTime: 30_000,
  })
  const ticketingOn = open && ticketingMode && searchable(query.trim())
  const ticketingQ = useQuery({
    queryKey: ['palette-ticketing-search', recordQuery],
    // Lazy: keeps the ticketing service out of the always-loaded shell bundle.
    queryFn: async () => (await import('@/modules/it-ticketing/services/ticketingSearch')).searchTicketing(recordQuery, TICKETING_LIMIT),
    enabled: open && ticketingMode && searchable(recordQuery),
    retry: false,
    staleTime: 30_000,
  })

  // Recompute results when query changes
  const results = useMemo<Scored[]>(() => {
    const recent = readRecent()
    const recentRank = (id: string) => recent.indexOf(id)
    const scored = catalog
      .map(entry => ({ entry, score: scoreEntry(entry, query.trim(), recentRank(entry.id)) }))
      .filter(s => s.score !== -Infinity)
    scored.sort((a, b) => b.score - a.score)
    // With no query inside IT Ticketing, offer the module's own pages.
    if (!query.trim() && ticketingMode) return scored.filter(s => s.entry.workspace === 'IT Ticketing')
    // When there's no query, only show recents (or first 12 entries if no recents)
    if (!query.trim()) {
      const recents = scored.filter(s => recent.includes(s.entry.id))
      if (recents.length > 0) return recents.slice(0, RECENT_LIMIT)
      return scored.slice(0, 12)
    }
    return scored.slice(0, 50)
  }, [query, catalog, ticketingMode])

  const recent = readRecent()
  const showingRecents = !query.trim() && !ticketingMode && results.some(r => recent.includes(r.entry.id))

  // The groups in display order. Keyboard navigation walks this same order.
  const groups = useMemo(() => {
    const out: Array<{ ws: CommandEntry['workspace']; label: string; items: Scored[] }> = []
    if (showingRecents) {
      out.push({ ws: 'Action', label: 'Recent', items: results })
      return out
    }
    if (ticketingOn) {
      const q = query.trim()
      // Only show hits for the query on screen, not a stale debounced one.
      const data = recordQuery === q ? ticketingQ.data : undefined
      const tickets: Scored[] = (data?.tickets ?? []).map((t) => ({
        entry: {
          id: `itt-${t.id}`, workspace: 'Tickets', title: `${t.ticketNumber} · ${t.title}`,
          subtitle: [t.status, t.priority, t.category].filter(Boolean).join(' · '),
          icon: <Ticket size={16} />, path: `/it-ticketing/${encodeURIComponent(t.id)}`, transient: true,
        },
        score: 0,
      }))
      tickets.push({
        entry: {
          id: 'itt-all', workspace: 'Tickets', title: `Search the ticket queue for "${q}"`, icon: <Search size={16} />,
          path: `/it-ticketing?q=${encodeURIComponent(q)}`, transient: true,
        },
        score: 0,
      })
      out.push({ ws: 'Tickets', label: 'Tickets', items: tickets })

      const people: Scored[] = (data?.people ?? []).map((p) => ({
        entry: {
          id: `itp-${p.id}`, workspace: 'People', title: p.name, subtitle: `${p.email ? `${p.email} · ` : ''}Open their assigned tickets`,
          icon: <UserRound size={16} />, path: `/it-ticketing?assignee=${encodeURIComponent(p.id)}`, transient: true,
        },
        score: 0,
      }))
      if (people.length > 0) out.push({ ws: 'People', label: 'People', items: people })

      const comments: Scored[] = (data?.comments ?? []).map((c) => ({
        entry: {
          id: `itc-${c.id}`, workspace: 'Comments', title: `${c.ticketNumber} · ${c.ticketTitle}`, subtitle: c.snippet,
          icon: <MessageSquare size={16} />, path: `/it-ticketing/${encodeURIComponent(c.ticketId)}`, transient: true,
        },
        score: 0,
      }))
      if (comments.length > 0) out.push({ ws: 'Comments', label: 'Comments', items: comments })
    }
    for (const ws of ticketingMode ? TICKETING_WORKSPACE_ORDER : WORKSPACE_ORDER) {
      const items = results.filter(r => r.entry.workspace === ws)
      if (items.length > 0) out.push({ ws, label: ws, items })
    }
    if (recordsOn) {
      const q = query.trim()
      // Only show hits for the query on screen, not a stale debounced one.
      const hits = recordQuery === q ? (recordsQ.data?.results ?? []) : []
      const items: Scored[] = hits.slice(0, RECORD_LIMIT).map((h) => ({ entry: recordEntry(h), score: 0 }))
      items.push({
        entry: {
          id: 'rec-all', workspace: 'Records', title: `Search all records for "${q}"`, icon: <Search size={16} />,
          path: `/workflow-management/search?q=${encodeURIComponent(q)}`, transient: true,
        },
        score: 0,
      })
      out.push({ ws: 'Records', label: 'Records', items })
    }
    return out
  }, [results, showingRecents, recordsOn, recordQuery, query, recordsQ.data, ticketingOn, ticketingMode, ticketingQ.data])
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups])
  const recordsBusy = recordsOn && (recordQuery !== query.trim() || recordsQ.isFetching)
  const ticketingBusy = ticketingOn && (recordQuery !== query.trim() || ticketingQ.isFetching)

  // Keep highlight in range
  useEffect(() => { setHighlighted(0) }, [query])
  useEffect(() => {
    if (highlighted >= flat.length) setHighlighted(Math.max(0, flat.length - 1))
  }, [flat.length, highlighted])

  const runEntry = useCallback((entry: CommandEntry) => {
    if (!entry.transient) pushRecent(entry.id)
    setOpen(false)
    if (entry.action) {
      entry.action()
    } else if (entry.path) {
      navigate(entry.path)
    }
  }, [navigate])

  function handleInputKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted(h => Math.min(h + 1, flat.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted(h => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const target = flat[highlighted]
      if (target) runEntry(target.entry)
    }
  }

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/40 flex items-start justify-center pt-[15vh] px-4"
      onMouseDown={(e) => { pressStartedOnBackdrop.current = e.target === e.currentTarget }}
      onMouseUp={(e) => {
        if (pressStartedOnBackdrop.current && e.target === e.currentTarget) setOpen(false)
        pressStartedOnBackdrop.current = false
      }}
    >
      <div
        className="bg-white rounded-xl shadow-card-hover w-full max-w-2xl overflow-hidden flex flex-col"
        role="dialog"
        aria-label="Command palette"
      >
        {/* Search bar */}
        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
          <Search size={18} className="text-text-tertiary flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleInputKey}
            placeholder={ticketingMode
              ? 'Search tickets, people, comments and pages…'
              : recordsAllowed ? 'Search pages, actions and records…' : 'Search workspaces, pages, actions…'}
            className="flex-1 outline-none text-body-md placeholder:text-text-tertiary bg-transparent"
          />
          <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-border text-text-tertiary">Esc</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {flat.length === 0 ? (
            <div className="px-4 py-8 text-center text-body-sm text-text-tertiary">
              No matches for <span className="font-medium">"{query}"</span>
            </div>
          ) : (
            (() => {
              let runningIdx = 0
              return groups.map(group => (
                <div key={group.label}>
                  <div className="px-4 pt-3 pb-1 flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold text-text-tertiary">
                    <span>{group.label}</span>
                    {group.ws === 'Records' && recordsBusy && <span className="normal-case tracking-normal font-normal">Searching…</span>}
                    {group.ws === 'Tickets' && ticketingBusy && <span className="normal-case tracking-normal font-normal">Searching…</span>}
                    {group.ws === 'Tickets' && ticketingQ.error && !ticketingBusy && (
                      <span className="normal-case tracking-normal font-normal text-status-red">{(ticketingQ.error as Error).message}</span>
                    )}
                    {group.ws === 'Records' && recordsQ.error && !recordsBusy && (
                      <span className="normal-case tracking-normal font-normal text-status-red">{(recordsQ.error as Error).message}</span>
                    )}
                  </div>
                  {group.items.map(item => {
                    const idx = runningIdx++
                    const isHighlighted = idx === highlighted
                    return (
                      <button
                        key={item.entry.id}
                        onClick={() => runEntry(item.entry)}
                        onMouseMove={() => setHighlighted(idx)}
                        className={cn(
                          'w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors',
                          isHighlighted ? 'bg-brand-50' : 'bg-white hover:bg-surface-muted/50'
                        )}
                      >
                        <span className={cn(
                          'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0',
                          isHighlighted ? 'bg-white text-brand-600' : 'bg-surface-muted text-text-secondary'
                        )}>
                          {item.entry.icon}
                        </span>
                        <span className="flex-1 min-w-0 text-body-sm">
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-text-primary truncate">{item.entry.title}</span>
                            {item.entry.cui && <CuiChip className="flex-shrink-0" />}
                            {!showingRecents && !item.entry.subtitle && group.ws !== 'Records' && (
                              <span className="text-text-tertiary text-[11px]">{item.entry.workspace}</span>
                            )}
                          </span>
                          {item.entry.subtitle && (
                            <span className="block truncate text-[11px] text-text-tertiary">{item.entry.subtitle}</span>
                          )}
                        </span>
                        {isHighlighted && <ArrowRight size={14} className="text-brand-600" />}
                      </button>
                    )
                  })}
                </div>
              ))
            })()
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-border bg-surface-muted/30 flex items-center justify-between text-[11px] text-text-tertiary">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="font-mono px-1 rounded border border-border bg-white"><ArrowUp size={10} className="inline" /></kbd>
              <kbd className="font-mono px-1 rounded border border-border bg-white"><ArrowDown size={10} className="inline" /></kbd>
              <span>navigate</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="font-mono px-1 rounded border border-border bg-white"><CornerDownLeft size={10} className="inline" /></kbd>
              <span>open</span>
            </span>
          </div>
          <span>{flat.length} result{flat.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>,
    document.body
  )
}
